import {every} from 'lodash';
import {ReactNode} from 'react';

import {HandlerOf, RendererOf} from '../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncMoreStatusesEnum} from '../../src/store/appTypes';

/*
 * Data types.
 */

export enum FocusModesEnum {
  TOP = 'TOP',
  MIDDLE = 'MIDDLE',
}

export enum ScrollDirectionsEnum {
  TOP = 'TOP',
  BOTTOM = 'BOTTOM',
}

/*
 * Component types.
 */

export interface BaseScrollerProps<TId> {
  focusedId?: TId;
  itemHeight: number;
  renderItem: RendererOf<TId>;

  moreStatuses: ScrollerMoreStatuses;
  loaderHeight: number;
  renderLoader: LoaderRenderer;
  onLoadMore: LoadMoreHandler;

  padding?: {top?: number; bottom?: number};
}

export interface UnidimensionalScrollerProps<TId> extends BaseScrollerProps<TId> {
  itemIds: ReadonlyArray<TId>;
}

export interface MultidimensionalScrollerProps<TId> extends BaseScrollerProps<TId> {
  itemIds: ReadonlyArray<ReadonlyArray<TId>>;
  sectionHeaderHeight: number;
  renderSectionHeader: (sectionIndex: number, topOffset: number) => ReactNode;
}

export type ScrollerProps<TId> = UnidimensionalScrollerProps<TId> | MultidimensionalScrollerProps<TId>;

interface BaseSyncScrollerProps<T> {
  itemHeight: number;
  getItemId?: (item: T) => string | number;
  renderItem: RendererOf<T>;
  padding?: {top?: number; bottom?: number};
}

interface SyncUnidimensionalScrollerProps<T> extends BaseSyncScrollerProps<T> {
  items: ReadonlyArray<T>;
}

interface SyncMultidimensionalScrollerProps<T> extends BaseSyncScrollerProps<T> {
  items: ReadonlyArray<ReadonlyArray<T>>;
  sectionHeaderHeight: number;
  renderSectionHeader: (sectionIndex: number, topOffset: number) => ReactNode;
}

export type SyncScrollerProps<T> = SyncUnidimensionalScrollerProps<T> | SyncMultidimensionalScrollerProps<T>;

export type ScrollerMoreStatuses = {[direction in ScrollDirectionsEnum]: AsyncMoreStatusesEnum};

export interface LoaderRendererOptions {
  direction: ScrollDirectionsEnum;
  moreStatus: AsyncMoreStatusesEnum;
}

export type LoaderRenderer = RendererOf<LoaderRendererOptions>;
export type LoadMoreHandler = HandlerOf<ScrollDirectionsEnum>;

/*
 * Type guards.
 */

export function isMultidimensionalArray<T>(
  arr: ReadonlyArray<T | ReadonlyArray<T>>,
): arr is ReadonlyArray<ReadonlyArray<T>> {
  return every(arr, (a: T | ReadonlyArray<T>) => Array.isArray(a));
}
